import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Flex, HStack, Select, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from "@chakra-ui/react";
import { RefreshCw } from "lucide-react";
import { Line } from "react-chartjs-2";
import { getMetricasAnunciosPainel } from "../../services/api";
import { COR_CONTA, ErroCarga, Kpi, VERDE, Vazio, dataHoraBR, diaBR, num } from "../SaudeAnunciosML/comum";
import {
  BadgeTipo, CONTAS, COR_CLASSICO, COR_PREMIUM, Cartao, CartaoGrafico, SeletorDias, TAMANHO_CAMPO, TAMANHO_PEQUENO,
  brl, conversao, opcoesLinha, serieLinha,
} from "./base";

const RANKINGS = [
  { chave: "mais_vistos", titulo: "Mais vistos", valor: (r) => `${num(r.visitas)} visitas` },
  { chave: "mais_vendidos", titulo: "Mais vendidos", valor: (r) => `${num(r.unidades)} un.` },
  { chave: "maior_conversao", titulo: "Maior conversão", valor: (r) => conversao(r.pedidos, r.visitas) },
  { chave: "visitas_sem_venda", titulo: "Visitas sem venda", valor: (r) => `${num(r.visitas)} visitas` },
  { chave: "maior_lucro", titulo: "Maior lucro", valor: (r) => brl(r.lucro) },
];

function Ranking({ titulo, itens, valor, onAbrir }) {
  return (
    <Cartao titulo={titulo}>
      {!itens?.length ? <Text fontSize="sm" color="gray.600">Sem dados no período.</Text> : (
        <VStack align="stretch" spacing={0}>
          {itens.map((r, i) => (
            <Box key={r.id_ml} as="button" type="button" textAlign="left" w="100%" px={2} py={2} borderRadius="md"
              _hover={{ bg: "gray.50" }} _focusVisible={{ boxShadow: "outline", outline: "none" }} onClick={() => onAbrir(r.id_ml)}>
              <Flex gap={2} align="flex-start">
                <Text fontSize="xs" color="gray.500" w="18px" flexShrink={0} mt="1px">{i + 1}</Text>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" color="gray.800" noOfLines={2}>{r.nome || r.id_ml}</Text>
                  <HStack spacing={1} mt={0.5} flexWrap="wrap">
                    <Text fontSize="xs" color="gray.600">{r.id_ml} · Conta {r.conta_id}</Text>
                    <BadgeTipo tipo={r.listing_type_id} />
                  </HStack>
                </Box>
                <Text fontSize="sm" fontWeight="semibold" flexShrink={0} sx={{ fontVariantNumeric: "tabular-nums" }}>{valor(r)}</Text>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </Cartao>
  );
}

function GraficoContas({ titulo, serie, campo, formatar = (v) => num(v), subtitulo }) {
  const dias = useMemo(() => [...new Set(serie.map((s) => String(s.data).slice(0, 10)))].sort(), [serie]);
  const porDia = useMemo(() => {
    const m = {};
    serie.forEach((s) => { m[`${String(s.data).slice(0, 10)}|${s.conta_id}`] = Number(s[campo] || 0); });
    return m;
  }, [serie, campo]);
  const contas = useMemo(() => [...new Set(serie.map((s) => Number(s.conta_id)))].sort(), [serie]);

  const dados = useMemo(() => ({
    labels: dias.map(diaBR),
    datasets: contas.map((c) => serieLinha(`Conta ${c}`, dias.map((d) => porDia[`${d}|${c}`] ?? 0), COR_CONTA[(c - 1) % COR_CONTA.length])),
  }), [dias, contas, porDia]);
  const opcoes = useMemo(() => opcoesLinha(formatar), [formatar]);

  if (!dias.length) return <Cartao titulo={titulo}><Text fontSize="sm" color="gray.600">Sem dados no período.</Text></Cartao>;
  return (
    <CartaoGrafico
      titulo={titulo}
      subtitulo={subtitulo}
      grafico={<Line data={dados} options={opcoes} role="img" aria-label={`${titulo} por dia e por conta`} />}
      tabela={(
        <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
          <Thead position="sticky" top={0} bg="white"><Tr><Th>Dia</Th>{contas.map((c) => <Th key={c} isNumeric>Conta {c}</Th>)}</Tr></Thead>
          <Tbody>
            {[...dias].reverse().map((d) => (
              <Tr key={d}><Td>{diaBR(d)}</Td>{contas.map((c) => <Td key={c} isNumeric>{formatar(porDia[`${d}|${c}`] ?? 0)}</Td>)}</Tr>
            ))}
          </Tbody>
        </Table>
      )}
    />
  );
}

export default function PainelTab({ onAbrir }) {
  const [periodo, setPeriodo] = useState(30);
  const [conta, setConta] = useState("");
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const ultima = useRef(0);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    const r = await getMetricasAnunciosPainel({ periodo, conta: conta || undefined });
    if (minha !== ultima.current) return;
    setCarregando(false);
    setErro(!r);
    if (r) setDados(r);
  }, [periodo, conta]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  const tipos = useMemo(() => {
    const t = { gold_pro: 0, gold_special: 0, outros: 0 };
    (dados?.tipos || []).forEach((l) => {
      if (l.tipo in t) t[l.tipo] += Number(l.n);
      else t.outros += Number(l.n);
    });
    return t;
  }, [dados]);

  const totais = useMemo(() => (dados?.totais || []).reduce((acc, l) => {
    ["visitas", "pedidos", "unidades", "receita", "lucro"].forEach((k) => { acc[k] = (acc[k] || 0) + Number(l[k] || 0); });
    return acc;
  }, {}), [dados]);

  const porConta = useMemo(() => {
    const linhas = {};
    CONTAS.forEach((c) => {
      linhas[c.id] = { id: c.id, nome: c.nome, ativos: 0, visitas: 0, pedidos: 0, unidades: 0, receita: 0, lucro: 0 };
    });
    (dados?.tipos || []).forEach((l) => {
      const linha = linhas[Number(l.conta_id)];
      if (linha) linha.ativos += Number(l.n);
    });
    (dados?.totais || []).forEach((l) => {
      const linha = linhas[Number(l.conta_id)];
      if (linha) ["visitas", "pedidos", "unidades", "receita", "lucro"].forEach((k) => { linha[k] += Number(l[k] || 0); });
    });
    return Object.values(linhas).filter((l) => !conta || String(l.id) === String(conta));
  }, [dados, conta]);

  const formatarBRL = useCallback((v) => brl(v, 0), []);
  const visitasCompletas = Number(dados?.coleta?.cobertura_visitas || 0) >= 0.9;

  return (
    <Box p={{ base: 3, md: 5 }}>
      <Flex gap={2} mb={4} flexWrap="wrap" align="center">
        <SeletorDias valor={periodo} onChange={setPeriodo} />
        <Select size={TAMANHO_CAMPO} w={{ base: "100%", sm: "180px" }} bg="white" value={conta} onChange={(e) => setConta(e.target.value)} aria-label="Conta">
          <option value="">Todas as contas</option>
          {CONTAS.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </Select>
        <Button size={TAMANHO_PEQUENO} variant="outline" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}>Atualizar</Button>
        {dados?.coleta && (
          <Text fontSize="xs" color="gray.600" ml={{ base: 0, md: "auto" }}>
            Visitas até {dados.coleta.visitas_ate ? diaBR(dados.coleta.visitas_ate) : "—"} · resumo de {dados.coleta.resumo_em ? dataHoraBR(dados.coleta.resumo_em) : "—"}
          </Text>
        )}
      </Flex>

      {erro && !dados && <ErroCarga onTentar={carregar} carregando={carregando} />}
      {!dados && !erro && <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>}

      {dados && (
        <VStack align="stretch" spacing={4}>
          {!visitasCompletas && (
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              Visitas coletadas em {num(dados.coleta?.anuncios_com_visitas_coletadas)} de {num(dados.coleta?.anuncios_ativos)} anúncios ativos.
              A coleta roda às 00:30 e traz os últimos 150 dias; até completar, visitas e conversão ficam parciais.
            </Alert>
          )}

          <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
            <Kpi label="Anúncios ativos" valor={num(tipos.gold_pro + tipos.gold_special + tipos.outros)}
              ajuda={`Premium ${num(tipos.gold_pro)} · Clássico ${num(tipos.gold_special)}`} cor={VERDE} />
            <Kpi label="Visitas" valor={num(totais.visitas)} cor={COR_CLASSICO} />
            <Kpi label="Vendas (un.)" valor={num(totais.unidades)} ajuda={`${num(totais.pedidos)} pedidos`} cor={VERDE} />
            <Kpi label="Receita" valor={brl(totais.receita, 0)} cor={VERDE} />
            <Kpi label="Lucro" valor={brl(totais.lucro, 0)} cor={Number(totais.lucro) < 0 ? "#C53030" : VERDE} />
            <Kpi label="Conversão" valor={visitasCompletas ? conversao(totais.pedidos, totais.visitas) : "—"}
              ajuda={visitasCompletas ? "pedidos / visitas" : "aguardando visitas"} cor={COR_PREMIUM} />
          </SimpleGrid>

          <Cartao titulo={`Por conta (${periodo} dias)`}>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Conta</Th>
                    <Th isNumeric>Ativos</Th>
                    <Th isNumeric>Visitas</Th>
                    <Th isNumeric>Vendas (un.)</Th>
                    <Th isNumeric>Receita</Th>
                    <Th isNumeric>Lucro</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {porConta.map((l) => (
                    <Tr key={l.id}>
                      <Td whiteSpace="nowrap">{l.nome}</Td>
                      <Td isNumeric>{num(l.ativos)}</Td>
                      <Td isNumeric>{num(l.visitas)}</Td>
                      <Td isNumeric>{num(l.unidades)}</Td>
                      <Td isNumeric>{brl(l.receita, 0)}</Td>
                      <Td isNumeric color={l.lucro < 0 ? "#C53030" : undefined}>{brl(l.lucro, 0)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Cartao>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <GraficoContas titulo="Visitas por dia" serie={dados.serie || []} campo="visitas" />
            <GraficoContas titulo="Vendas por dia (unidades)" serie={dados.serie || []} campo="unidades" />
            <GraficoContas titulo="Receita por dia" serie={dados.serie || []} campo="receita" formatar={formatarBRL} />
            <GraficoContas titulo="Lucro por dia" serie={dados.serie || []} campo="lucro" formatar={formatarBRL}
              subtitulo="Receita menos custo, tarifa real do ML, frete e imposto (DRE), rateado por item." />
          </SimpleGrid>

          {Object.values(dados.rankings || {}).every((l) => !l?.length) ? (
            <Vazio titulo="Rankings aparecem depois do primeiro resumo." />
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {RANKINGS.map((r) => (
                <Ranking key={r.chave} titulo={`${r.titulo} (${periodo} dias)`} itens={dados.rankings?.[r.chave]} valor={r.valor} onAbrir={onAbrir} />
              ))}
            </SimpleGrid>
          )}
        </VStack>
      )}
    </Box>
  );
}
